/**
 * Actions
 *
 * ActionCreators are like the "pub" of pubsub. It's just broadcasting an event (action).
 * So, don't couple it with a specific reducer. Let ANY reducer listen to
 * an action.
 *
 * `typesafe-actions` has a nice utility function so we don't need to define action types as
 * constants. Use it together with `getType(type)` in the reducers.
 */
import { createAction } from "typesafe-actions";
import {
  PaymentType,
  RefundType,
  EditPaymentPayeeRequest,
  ScheduleType,
  UpcomingPaymentType,
  FeesVerboseType,
  PaymentState,
  LogChargeAttempt,
  PayoutPaymentsRequestType,
  PaymentCommentType,
  ScheduleRequestType,
  exchangeRateDetailType,
  BulkRequestType,
  RefundRequestType,
  DbsUapiReviewResult
} from "./reducers";
import { PayeeType } from "../Payee/reducers";
import { PAYMENT_STATUS } from "./const";

// Legacy
// export const fetchPayments = createAction("payments/FETCH", resolve => () =>
//   resolve()
// );

export const setPayments = createAction(
  "payments/SET",
  resolve => (args: {
    isFetching: boolean;
    payments?: PaymentType[];
    total?: number;
    sumPaymentAmount?: number;
    sumPaymentTotal?: number;
    totalBankPayout?: number;
    totalStripeTransactions?: number;
    totalBatch?: number;
  }) => resolve(args)
);

export const fetchDashboardPayments = createAction(
  "payments/FETCH_DASHBOARD",
  resolve => () => resolve()
);

export const appendPayments = createAction(
  "payments/APPEND",
  resolve => (args: { isFetching: boolean; payments: PaymentType[] }) =>
    resolve(args)
);

export const setRefundRequests = createAction(
  "payments/SET_PAYMENTS_REFUND",
  resolve => (args: {
    isFetching: boolean;
    refundRequests: RefundType[] | EditPaymentPayeeRequest[];
    totalRequest: number;
  }) => resolve(args)
);

export const setRefundRequesteds = createAction(
  "payments/SET_PAYMENTS_REFUND_REQUESTED",
  resolve => (args: {
    isFetching: boolean;
    refundRequesteds: RefundType[] | EditPaymentPayeeRequest[];
  }) => resolve(args)
);

export const fetchAdminPayments = createAction(
  "payments/ADMIN_FETCH",
  resolve => (offset: number = 0) => resolve({ offset })
);

export const adminExportPayment = createAction(
  "payments/ADMIN_EXPORT_PAYMENT",
  resolve => (type: "dbs" | "international_dbs") => resolve({ type })
);

export const adminExportDailyPayment = createAction(
  "payments/ADMIN_EXPORT_DAILY_PAYMENT"
);

export const adminExportCitibankCBFT = createAction(
  "payments/ADMIN_EXPORT_CITIBANK_CBFT"
);

export const adminExportHongleongXLS = createAction(
  "payments/ADMIN_EXPORT_HONGLEONG_XLS"
);

export const adminExportWeeklyStatement = createAction(
  "payments/ADMIN_EXPORT_WEEKLY_STATEMENT"
);

export const adminPaymentsForApproval = createAction(
  "payments/ADMIN_PAYMENTS_FOR_APPROVAL"
);

export const markPaymentAsCompleted = createAction(
  "payments/ADMIN_MARK_AS_COMPLETED",
  resolve => (args: { accountId: number; paymentId: number }) => resolve(args)
);

export const markPaymentAsCompletedWithDownloadCSV = createAction(
  "payments/ADMIN_MARK_AS_COMPLETED_WITHOUT_DOWNLOAD_FILE",
  resolve => (args: { accountId: number; paymentId: number }) => resolve(args)
);

export const markBulkPaymentsAsCompleted = createAction(
  "payments/ADMIN_MARK_BULK_PAYMENTS_AS_COMPLETED",
  resolve => (
    payments: Array<{
      account_id: number;
      payment_id: number;
    }>
  ) => resolve(payments)
);

export const markBulkPaymentsAsCompletedWithDownloadCSV = createAction(
  "payments/ADMIN_MARK_BULK_PAYMENTS_AS_COMPLETED_WITH_DOWNLOAD_CSV",
  resolve => (
    payments: Array<{
      account_id: number;
      payment_id: number;
    }>
  ) => resolve(payments)
);

export const adminBulkUpdatePaymentsStatus = createAction(
  "payments/ADMIN_BULK_UPDATE_PAYMENTS_STATUS",
  resolve => (args: {
    paymentIds: number[];
    statusId: number;
    cb?: (err?: any) => void;
  }) => resolve(args)
);

// DBS UAPI (maker): move the In Progress payments matching the current UAPI
// filters to Payout Review. Nothing is sent to DBS.
export const adminDbsUapiReview = createAction(
  "payments/ADMIN_DBS_UAPI_REVIEW",
  resolve => (args?: { cb?: (err?: any) => void }) => resolve(args || {})
);

// DBS UAPI (checker): approve the Payout Review payments matching the current
// UAPI filters. The backend sends them to DBS (Payout Pending) and flips them to
// Payout Completed on its own once DBS confirms.
export const adminDbsUapiApprove = createAction(
  "payments/ADMIN_DBS_UAPI_APPROVE",
  resolve => (args?: { cb?: (err?: any) => void }) => resolve(args || {})
);

// DBS UAPI: mark a single DBS-confirmed payout (status 33) as completed.
export const adminDbsUapiComplete = createAction(
  "payments/ADMIN_DBS_UAPI_COMPLETE",
  resolve => (args: { paymentId: number; cb?: (err?: any) => void }) =>
    resolve(args)
);

export const setDbsUapiReviewResult = createAction(
  "payments/SET_DBS_UAPI_REVIEW_RESULT",
  resolve => (args: { result: DbsUapiReviewResult | null }) => resolve(args)
);

export const selectPayment = createAction(
  "payments/SELECT_PAYMENT",
  resolve => (id: number) => resolve({ id })
);

export const selectNextPayment = createAction("payments/SELECT_NEXT_PAYMENT");

export const adminSelectPayment = createAction(
  "payments/ADMIN_SELECT_PAYMENT",
  resolve => (id: number) => resolve({ id })
);

export const adminSelectNextPayment = createAction(
  "payments/ADMIN_SELECT_NEXT_PAYMENT"
);

export const adminSelectRefundRequestPaymentDetail = createAction(
  "payments/SELECT_REFUND_REQUEST_PAYMENT_DETAIL",
  resolve => (id: number) => resolve({ id })
);

export const adminSelectNextRefundRequest = createAction(
  "payments/SELECT_NEXT_REFUND_REQUEST"
);

export const adminEditPaymentPayee = createAction(
  "payments/ADMIN_EDIT_PAYMENT_PAYEE",
  resolve => (
    isSuccess: any,
    isRequireAccountId?: boolean,
    accountId?: number
  ) => resolve({ isSuccess, isRequireAccountId, accountId })
);

export const updatePaymentDetail = createAction(
  "payments/UPDATE_PAYMENT_DETAIL",
  resolve => (id: number, payment?: PaymentType) => resolve({ payment, id })
);

export const cancelSchedule = createAction(
  "payments/CANCEL_SCHEDULE",
  resolve => (id: number) => resolve({ id })
);

export const cancelPayments = createAction("payments/CANCEL_PAYMENTS");

export const fullyRefund = createAction(
  "payments/FULLY_REFUND",
  resolve => (id: number) => resolve({ id })
);

export const partialRefundPrincipal = createAction(
  "payments/PARTIAL_REFUND_PRINCIPAL",
  resolve => (id: number, payees: {}, currentFee: number) =>
    resolve({ id, payees, currentFee })
);

export const partialRefundFee = createAction(
  "payment/PARTIAL_REFUND_FEE",
  resolve => (id: number) => resolve({ id })
);

export const partialRefundPrincipalFees = createAction(
  "payments/PARTIAL_REFUND_PRINCIPAL_FEES",
  resolve => (
    id: number,
    payees: PayeeType[],
    currentFee: number,
    newRate: number | null,
    refundedFeeAmount: number | null
  ) =>
    resolve({
      id,
      payees,
      currentFee,
      newRate,
      refundedFeeAmount
    })
);

export const fetchPendingRefundRequests = createAction(
  "payments/ADMIN_GET_PENDING_REFUND_REQUESTS"
);

export const fetchPendingRefundRequesteds = createAction(
  "payments/ADMIN_GET_REFUND_REQUESTEDS"
);

export const processRefund = createAction(
  "payments/PROCESS_REFUND",
  resolve => (
    paymentId: number,
    requestId: number,
    status: string,
    scheduleId?: number,
    actionType?: string
  ) => resolve({ paymentId, requestId, status, scheduleId, actionType })
);

export const expeditePayoutDate = createAction(
  "payments/EXPEDITE_PAYOUT_DATE",
  resolve => (paymentId: number) => resolve({ paymentId })
);

export const holdPayment = createAction(
  "payment/HOLD_PAYMENT",
  resolve => (paymentId: number) => resolve({ paymentId })
);

export const bulkHoldPayment = createAction(
  "payments/ADMIN_BULK_HOLD_PAYMENT",
  resolve => (
    payments: Array<{
      payment_id: number;
    }>
  ) => resolve(payments)
);

export const adminCancelPayment = createAction(
  "payment/ADMIN_CANCEL_PAYMENT",
  resolve => (paymentId: number, statusId: number, userEmail?: string) =>
    resolve({ paymentId, statusId, userEmail })
);

export const adminCheckPayment = createAction(
  "payment/CHECK_PAYMENT",
  resolve => (cb: (res: object) => void) => resolve({ cb })
);

export const setPaymentActivity = createAction(
  "payment/SET_ACTIVITY",
  resolve => (activity: PaymentState["activity"]) => resolve({ activity })
);

export const setSumPaymentInThisMonth = createAction(
  "payment/SET_AMOUNT_THIS_MONTH",
  resolve => (sumPaymentInThisMonth: PaymentState["sumPaymentInThisMonth"]) =>
    resolve({ sumPaymentInThisMonth })
);

// Scheduled Payment
export const fetchSchedules = createAction(
  "payments/FETCH_SCHEDULES",
  resolve => (isActive: string = "y", offset: number = 0, cb?: () => void) =>
    resolve({ isActive, offset, cb })
);

export const setSchedules = createAction(
  "payments/SET_SCHEDULES",
  resolve => (args: {
    isFetching: boolean;
    schedules: ScheduleType[];
    total?: number;
  }) => resolve(args)
);

export const setScheduleDetail = createAction(
  "payments/SET_UPCOMING_PAYMENT",
  resolve => (args: {
    payments: UpcomingPaymentType[];
    paymentSetting: PaymentType;
    lastPayout: string;
    scheduleId: number;
    accountId?: number;
    upComingPayoutDate?: string;
  }) => resolve(args)
);

export const selectSchedule = createAction(
  "payments/SELECT_SCHEDULE",
  resolve => (id: number) => resolve({ id })
);

export const selectNextSchedule = createAction("payments/SELECT_NEXT_SCHEDULE");

export const selectPayments = createAction(
  "payments/SELECT_PAYMENTS",
  resolve => (ids: number[]) => resolve({ ids })
);

export const editPayments = createAction(
  "payments/EDIT_PAYMENTS",
  resolve => (ids: number[]) => resolve({ ids })
);

export const checkFeeVerbose = createAction(
  "payments/CHECK_FEE_VERBOSE",
  resolve => (
    cardId: number,
    couponCode: string,
    cb?: (coupon: string, fee: number) => void
  ) => resolve({ cardId, couponCode, cb })
);

export const setFeesVerbose = createAction(
  "payments/SET_FEES_VERBOSE",
  resolve => (feesVerbose: { [paymentId: number]: FeesVerboseType }) =>
    resolve({ feesVerbose })
);

export const couponUsageVerbose = createAction(
  "payments/COUPON_USAGE_VERBOSE",
  resolve => (cardId: number, cb?: (couponCode: string) => void) =>
    resolve({ cardId, cb })
);

export const validatePayee = createAction("payments/VALIDATE_PAYEE");

export const depositInternationalPayments = createAction(
  "payments/DEPOSIT_INTERNATIONAL_PAYMENT",
  resolve => (paymentId: number[]) => resolve({ paymentId })
);

export const setUpdatedPaymentStatus = createAction(
  "payments/UPDATED_PAYMENT_STATUS",
  resolve => (updatedPaymentStatus: { success: boolean; failed: boolean }) =>
    resolve({ updatedPaymentStatus })
);

export const fetchPaymentsHistoryList = createAction(
  "payments/FETCH_PAYMENTS_HISTORY_LIST",
  resolve => (offset: number = 0) => resolve({ offset })
);

export const fetchPaymentsHistoryExport = createAction(
  "payments/FETCH_PAYMENTS_HISTORY_EXPORT"
);

export const fetchLogChargeAttempt = createAction(
  "payments/FETCH_LOG_CHARGE_ATTEMPT",
  resolve => (id: number) => resolve({ id })
);

export const setLogChargeAttempt = createAction(
  "payments/SET_LOG_CHARGE_ATTEMPT",
  resolve => (logChargeAttempt: LogChargeAttempt) =>
    resolve({ logChargeAttempt })
);

// Payout Payments
export const fetchPayoutPaymentsRequests = createAction(
  "payments/ADMIN_GET_PAYOUT_PAYMENTS_REQUESTS"
);

export const setPayoutPaymentsRequests = createAction(
  "payments/SET_PAYOUT_PAYMENTS_REQUESTS",
  resolve => (args: {
    isFetching: boolean;
    payoutPaymentsRequests: PayoutPaymentsRequestType[];
  }) => resolve(args)
);

export const processPayoutPaymentsRequest = createAction(
  "payments/PROCESS_PAYOUT_PAYMENTS_REQUEST",
  resolve => (requestId: number, status: string) =>
    resolve({ requestId, status })
);

// Payment comment
export const fetchAdminPaymentComments = createAction(
  "payments/ADMIN_FETCH_PAYMENT_COMMENTS",
  resolve => (id: number) => resolve({ id })
);

export const adminAddPaymentComment = createAction(
  "payments/ADMIN_ADD_PAYMENT_COMMENT",
  resolve => (id: number) => resolve({ id })
);

export const setSelectedPaymentComments = createAction(
  "payments/SET_SELECTED_PAYMENT_COMMENTS",
  resolve => (args: {
    isFetching: boolean;
    selectedPaymentComments: PaymentCommentType[];
  }) => resolve(args)
);

export const adminBulkUploadPaymentFile = createAction(
  "payments/ADMIN_BULK_UPLOAD_PAYMENT_FILE",
  resolve => (
    isSuccess: any,
    isRequireAccountId?: boolean,
    accountId?: number
  ) => resolve({ isSuccess, isRequireAccountId, accountId })
);

export const adminFetchEnterprisePayments = createAction(
  "payments/ADMIN_FETCH_ENTERPRISE_PAYMENTS",
  resolve => (offset: number = 0) => resolve({ offset })
);

export const setEnterprisePayments = createAction(
  "payments/SET_ENTERPRISE_PAYMENTS",
  resolve => (args: {
    isFetching: boolean;
    enterprisePayments?: PaymentType[];
    payments?: PaymentType[];
    total?: number;
    sumPaymentAmount?: number;
    sumPaymentTotal?: number;
    totalBankPayout?: number;
    totalStripeTransactions?: number;
  }) => resolve(args)
);

export const adminCancelSchedule = createAction(
  "payments/ADMIN_CANCEL_SCHEDULE",
  resolve => (id: number, cb?: (err?: any) => void) => resolve({ id, cb })
);

export const adminFetchSchedules = createAction(
  "payments/ADMIN_FETCH_SCHEDULES",
  resolve => (offset: number = 0, cb?: () => void) => resolve({ offset, cb })
);

export const adminUpdateSchedule = createAction(
  "payments/ADMIN_UPDATE_SCHEDULE",
  resolve => (id: number, type: string) => resolve({ id, type })
);

export const adminSelectSchedule = createAction(
  "payments/ADMIN_SELECT_SCHEDULE",
  resolve => (id: number) => resolve({ id })
);

export const adminUpdateSchedulePayout = createAction(
  "payments/ADMIN_UPDATE_SCHEDULE_PAYOUT",
  resolve => (id: number) => resolve({ id })
);

export const fetchPendingScheduleRequests = createAction(
  "payments/ADMIN_GET_PENDING_SCHEDULE_REQUESTS"
);

export const setScheduleRequests = createAction(
  "payments/SET_SCHEDULE_REQUESTS",
  resolve => (args: {
    isFetching: boolean;
    scheduleRequests: ScheduleRequestType[];
    totalRequests: number;
  }) => resolve(args)
);

export const adminChangeStatus = createAction(
  "payments/ADMIN_CHANGE_STATUS",
  resolve => (args: {
    paymentId: number;
    paymentStatusId: PAYMENT_STATUS.COMPLETED | PAYMENT_STATUS.IN_PROGRESS_2;
  }) => resolve(args)
);

export const adminGetInstantPayLimit = createAction(
  "payments/ADMIN_GET_INSTANT_PAY_LIMIT"
);

export const setInstantPayLimit = createAction(
  "payments/SET_INSTANT_PAY_LIMIT",
  resolve => (args: { isFetching: boolean; instantPayLimit: number }) =>
    resolve(args)
);

export const adminUpdateInstantPayLimit = createAction(
  "payments/ADMIN_UPDATE_INSTANT_PAY_LIMIT"
);

export const adminBulkUploadPaymentFileSchedule = createAction(
  "payments/ADMIN_BULK_UPLOAD_PAYMENT_FILE_SCHEDULE",
  resolve => (
    isSuccess: any,
    isRequireAccountId?: boolean,
    accountId?: number
  ) => resolve({ isSuccess, isRequireAccountId, accountId })
);

export const adminGetExchangeRateDetail = createAction(
  "payments/ADMIN_GET_EXCHANGE_RATE_DETAIL",
  resolve => (id: number) => resolve({ id })
);

export const setExchangeRateDetail = createAction(
  "payments/SET_EXCHANGE_RATE_DETAIL",
  resolve => (exchangeRateDetail: exchangeRateDetailType) =>
    resolve({ exchangeRateDetail })
);

export const reinitiateWallexPayment = createAction(
  "payments/REINITIATE_WALLEX_PAYMENT",
  resolve => (paymentId: number, cb?: () => void) => resolve({ paymentId, cb })
);
export const selectSettlementCurrency = createAction(
  "payments/SELECT_SETTLEMENT_CURRENCY",
  resolve => (selectedSettlementCurrency: string) =>
    resolve({ selectedSettlementCurrency })
);

export const fetchPendingBulkRequests = createAction(
  "payments/ADMIN_GET_PENDING_BULK_REQUESTS"
);

export const setBulkRequests = createAction(
  "payments/SET_BULK_REQUESTS",
  resolve => (args: {
    isFetching: boolean;
    bulkRequests: BulkRequestType[];
    totalRequests: number;
  }) => resolve(args)
);

export const processBulkRequests = createAction(
  "payments/PROCESS_BULK_REQUESTS",
  resolve => (requestId: number, status: string, actionType: string) =>
    resolve({ requestId, status, actionType })
);

export const setRefundRequestsHistoryAppproved = createAction(
  "payments/SET_REFUND_REQUESTS_HISTORY_APPROVED",
  resolve => (args: {
    isFetching: boolean;
    refundRequestsHistoryAppproved: RefundRequestType[];
  }) => resolve(args)
);

export const fetchRefundRequestsHistoryAppproved = createAction(
  "payments/ADMIN_GET_REFUND_REQUESTS_HISTORY_APPROVED"
);

export const setScheduleRequesHistoryApproved = createAction(
  "payments/SET_SCHEDULE_REQUESTS_HISTORY_APPROVED",
  resolve => (args: {
    isFetching: boolean;
    scheduleRequestsHistoryApproved: ScheduleRequestType[];
  }) => resolve(args)
);

export const fetchScheduleRequestsHistoryAppproved = createAction(
  "payments/ADMIN_GET_SCHEDULE_REQUESTS_HISTORY_APPROVED"
);

export const setBulkRequesHistoryApproved = createAction(
  "payments/SET_BULK_REQUESTS_HISTORY_APPROVED",
  resolve => (args: {
    isFetching: boolean;
    bulkRequestHistoryApproved: BulkRequestType[];
  }) => resolve(args)
);

export const fetchBulkRequestsHistoryApproved = createAction(
  "payments/ADMIN_GET_BULK_REQUESTS_HISTORY_APPROVED"
);

export const adminEditPaymentFXRate = createAction(
  "payments/ADMIN_EDIT_PAYMENT_FX_RATE",
  resolve => (id: number) => resolve({ id })
);
